import { supabase } from '@/integrations/supabase/client';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
// Reuse the existing supplier-8d edge function and add a one-off route under it.
const SUPPLIER_PORTAL_FUNCTION = 'supplier-8d';

export interface SupplierComplaintBridgeClaim {
  id: string;
  claim_number: string;
  title: string;
  status: string;
  created_at: string;
  updated_at: string;
  magic_token: string;
  magic_token_expires_at: string;
}

/**
 * QUICK AND DIRTY ONE-OFF:
 * Bridge endpoint used to surface creator-tenant 8D claims
 * in the customer complaints page for the supplier tenant.
 *
 * We intentionally call an edge function instead of direct table reads because
 * client-side reads are correctly constrained by tenant RLS.
 */
export async function fetchSupplierComplaintBridgeClaims(): Promise<SupplierComplaintBridgeClaim[]> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error('Missing Supabase configuration');
  }

  const { data: { session } } = await supabase.auth.getSession();
  // Pass logged-in user JWT so the edge function can verify caller identity.
  const accessToken = session?.access_token;

  if (!accessToken) {
    throw new Error('Authentication required');
  }

  // One-off route implemented in supabase/functions/supplier-8d/handlers/bridge-claims.ts
  const response = await fetch(`${SUPABASE_URL}/functions/v1/${SUPPLIER_PORTAL_FUNCTION}/bridge-claims`, {
    method: 'GET',
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const payload = await response.json();
      if (payload?.error) {
        message = payload.error;
      }
    } catch {
      // ignore parse failures
    }
    throw new Error(message);
  }

  const payload = await response.json();
  if (!Array.isArray(payload)) {
    return [];
  }

  return payload as SupplierComplaintBridgeClaim[];
}
